import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2, Search, MoreHorizontal, MapPin } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const EMPTY = {
  name: '', street_address: '', suburb: '', state: '', postcode: '',
  contact_name: '', contact_phone: '', notes: '', is_active: true,
};

const DepotsTab = ({ api }) => {
  const [depots, setDepots] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchDepots = async () => {
    try {
      const res = await api.get('/depots');
      setDepots(res.data || []);
    } catch {
      toast.error('Failed to fetch depots');
    }
  };

  useEffect(() => { fetchDepots(); }, [api]);

  const openDialog = (depot = null) => {
    if (depot) {
      setEditing(depot);
      setForm({ ...EMPTY, ...depot });
    } else {
      setEditing(null);
      setForm(EMPTY);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/depots/${editing.id}`, form);
        toast.success('Depot updated');
      } else {
        await api.post('/depots', form);
        toast.success('Depot created');
      }
      setDialogOpen(false);
      fetchDepots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (depot) => {
    if (!confirm(`Delete depot "${depot.name}"?`)) return;
    try {
      await api.delete(`/depots/${depot.id}`);
      toast.success('Depot deleted');
      fetchDepots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete depot');
    }
  };

  const toggleActive = async (depot) => {
    try {
      await api.put(`/depots/${depot.id}`, { is_active: !depot.is_active });
      toast.success(`Depot ${depot.is_active ? 'deactivated' : 'activated'}`);
      fetchDepots();
    } catch {
      toast.error('Failed to update depot');
    }
  };

  const filtered = depots.filter(d => d.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const addressLine = (d) => [d.street_address, d.suburb, d.state, d.postcode].filter(Boolean).join(', ') || '—';

  return (
    <>
      <Card className="card-industrial">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Depots</CardTitle>
              <CardDescription>Manage depot locations that assets and jobs can be assigned to</CardDescription>
            </div>
            <Button className="btn-volt" onClick={() => openDialog()} data-testid="add-depot-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Depot
            </Button>
          </div>
          <div className="relative max-w-sm mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="depot-search"
              placeholder="Search depots..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(depot => (
                <TableRow key={depot.id} data-testid={`depot-row-${depot.id}`}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#ffd108]" />
                      {depot.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{addressLine(depot)}</TableCell>
                  <TableCell className="text-sm">
                    {depot.contact_name || '—'}
                    {depot.contact_phone && <span className="text-muted-foreground"> · {depot.contact_phone}</span>}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={depot.is_active ? 'default' : 'secondary'}
                      className={depot.is_active ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : ''}
                    >
                      {depot.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openDialog(depot)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleActive(depot)}>
                          {depot.is_active ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(depot)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No matching depots found' : 'No depots configured. Add your first one!'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Depot' : 'Add Depot'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="depot-form">
            <div>
              <Label htmlFor="depot-name">Name <span className="text-red-500">*</span></Label>
              <Input
                id="depot-name"
                data-testid="depot-name-input"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., North Depot"
                required
              />
            </div>
            <div>
              <Label htmlFor="depot-street">Street Address</Label>
              <Input id="depot-street" value={form.street_address}
                onChange={(e) => setForm(prev => ({ ...prev, street_address: e.target.value }))} placeholder="12 Rail St" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="depot-suburb">Suburb</Label>
                <Input id="depot-suburb" value={form.suburb}
                  onChange={(e) => setForm(prev => ({ ...prev, suburb: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="depot-state">State</Label>
                <Input id="depot-state" value={form.state}
                  onChange={(e) => setForm(prev => ({ ...prev, state: e.target.value }))} placeholder="NSW" />
              </div>
              <div>
                <Label htmlFor="depot-postcode">Postcode</Label>
                <Input id="depot-postcode" value={form.postcode}
                  onChange={(e) => setForm(prev => ({ ...prev, postcode: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="depot-contact">Contact Name</Label>
                <Input id="depot-contact" value={form.contact_name}
                  onChange={(e) => setForm(prev => ({ ...prev, contact_name: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="depot-phone">Contact Phone</Label>
                <Input id="depot-phone" value={form.contact_phone}
                  onChange={(e) => setForm(prev => ({ ...prev, contact_phone: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label htmlFor="depot-notes">Notes</Label>
              <Textarea id="depot-notes" rows={2} value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Optional notes..." />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="depot-active" checked={form.is_active}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))} />
              <Label htmlFor="depot-active">Active</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-volt" data-testid="depot-submit-btn">
                {editing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DepotsTab;
